import { Controller } from "@hotwired/stimulus"

const DEFAULT_MAX_LINE_QUANTITY = 25
const DEFAULT_MAX_RECEIPT_LINES = 20

export default class extends Controller {
  static targets = ["categoryList", "itemList", "composer", "receiptList", "receiptTotal", "couponList", "naturalPrompt", "naturalStatus", "naturalSubmit", "naturalTranscript"]
  static values = {
    inventory: Object,
    interpretationUrl: String,
    greeting: String,
    example: String,
    maxLineQuantity: Number,
    maxReceiptLines: Number
  }

  connect() {
    this.categories = this.inventoryValue?.categories || []
    this.receipt = []
    this.coupons = this.inventoryValue?.coupons || []
    this.appliedCouponIds = []
    this.selectedCategoryId = this.categories[0]?.id || null
    this.pendingItem = null
    this.pendingSelections = {}
    this.pendingOptionPositions = {}
    this.naturalContext = null
    this.naturalTurns = []
    this.render()
    this.appendNaturalTurn("MENU", this.hasGreetingValue ? this.greetingValue : "Tell me an item and any options you already know.")
  }

  interpretPrompt(event) {
    event.preventDefault()
    if (!this.hasNaturalPromptTarget || !this.hasInterpretationUrlValue) return

    const prompt = this.naturalPromptTarget.value.trim()
    if (!prompt) {
      this.setNaturalStatus("Enter an item request first.")
      this.naturalPromptTarget.focus()
      return
    }

    if (this.hasNaturalSubmitTarget) this.naturalSubmitTarget.disabled = true
    this.appendNaturalTurn("YOU", prompt)
    this.setNaturalStatus(this.naturalContext ? "Checking your answer..." : "Reading the live menu...")

    fetch(this.interpretationUrlValue, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      body: JSON.stringify({ prompt, context: this.naturalContext })
    }).then(async (response) => {
      const payload = await response.json().catch(() => ({}))
      if (!response.ok || !payload.success) {
        const error = payload.error?.message || payload.error || "Unable to interpret that order."
        throw new Error(error)
      }
      return payload.data
    }).then((interpretation) => {
      if (interpretation.status === "clarification_required") {
        this.naturalContext = interpretation.context
        this.naturalPromptTarget.value = ""
        this.naturalPromptTarget.placeholder = "Type one menu choice"
        this.setNaturalStatus(interpretation.message)
        this.appendNaturalTurn("MENU", interpretation.message)
        this.naturalPromptTarget.focus()
        return
      }

      if (interpretation.status !== "ready" || !interpretation.order) {
        throw new Error("The menu returned an incomplete order.")
      }

      const receiptResult = this.applyInterpretedOrder(interpretation.order)
      this.naturalContext = null
      this.naturalPromptTarget.value = ""
      this.naturalPromptTarget.placeholder = this.hasExampleValue ? this.exampleValue : "Tell me an item from this menu"
      const message = receiptResult.truncated
        ? `Receipt limited to ${this.maxLineQuantity} identical items per line and ${this.maxReceiptLines} distinct lines.`
        : (interpretation.message || "Receipt built. Review every item before checkout.")
      this.setNaturalStatus(message)
      this.appendNaturalTurn("MENU", message)
    }).catch((error) => {
      this.setNaturalStatus(error.message)
      this.appendNaturalTurn("MENU", error.message)
    }).finally(() => {
      if (this.hasNaturalSubmitTarget) this.naturalSubmitTarget.disabled = false
    })
  }

  applyInterpretedOrder(order) {
    let requestedQuantity = 0
    let addedQuantity = 0

    ;(order.lines || []).forEach((line) => {
      const item = this.categories
        .flatMap((category) => category.items || [])
        .find((candidate) => candidate.id === Number(line.item_id))
      if (!item) return

      const selectedOptions = (line.selections || []).map((selection) => {
        const group = item.modifier_groups.find((candidate) => candidate.id === Number(selection.modifier_group_id))
        const option = group?.options.find((candidate) => candidate.id === Number(selection.option_id))
        if (!group || !option) return null
        const positionId = selection.position_id ? Number(selection.position_id) : null
        return {
          group_id: group.id,
          group_name: group.name,
          id: option.id,
          name: option.name,
          price_delta: Number(option.price_delta || 0),
          position_id: positionId,
          position_name: this.positionNameFor(group, positionId),
          position_price_delta: this.positionPriceFor(option, positionId)
        }
      }).filter(Boolean)

      const quantity = Math.max(1, Math.floor(Number(line.quantity) || 1))
      requestedQuantity += quantity
      addedQuantity += this.addReceiptLine(item, selectedOptions, quantity)
    })

    const couponCodes = (order.coupon_codes || []).map((code) => String(code).toUpperCase())
    this.appliedCouponIds = this.coupons
      .filter((coupon) => couponCodes.includes(String(coupon.code).toUpperCase()))
      .map((coupon) => coupon.id)
    this.renderReceipt()
    return { addedQuantity, truncated: addedQuantity < requestedQuantity }
  }

  setNaturalStatus(message) {
    if (this.hasNaturalStatusTarget) this.naturalStatusTarget.textContent = String(message || "")
  }

  appendNaturalTurn(speaker, message) {
    if (!this.hasNaturalTranscriptTarget) return
    this.naturalTurns.push({ speaker: String(speaker), message: String(message || "") })
    this.naturalTurns = this.naturalTurns.slice(-8)
    this.naturalTranscriptTarget.innerHTML = ""
    this.naturalTurns.forEach((turn) => {
      const row = document.createElement("p")
      const label = document.createElement("b")
      const copy = document.createElement("span")
      label.textContent = turn.speaker
      copy.textContent = turn.message
      row.append(label, copy)
      this.naturalTranscriptTarget.append(row)
    })
    this.naturalTranscriptTarget.scrollTop = this.naturalTranscriptTarget.scrollHeight
  }

  selectCategory(event) {
    this.selectedCategoryId = Number(event.currentTarget.dataset.categoryId)
    this.pendingItem = null
    this.pendingSelections = {}
    this.pendingOptionPositions = {}
    this.render()
  }

  chooseItem(event) {
    const item = this.currentItems.find((entry) => entry.id === Number(event.currentTarget.dataset.itemId))
    if (!item) return

    if (!item.modifier_groups.length) {
      this.addReceiptLine(item, [])
      return
    }

    this.pendingItem = item
    this.pendingSelections = {}
    this.pendingOptionPositions = {}

    item.modifier_groups.forEach((group) => {
      if (group.required && !group.multi_select && group.options.length) {
        this.pendingSelections[group.id] = [group.options[0].id]
        this.assignDefaultPosition(group, group.options[0].id)
      } else {
        this.pendingSelections[group.id] = []
      }
    })

    this.renderComposer()
  }

  toggleOption(event) {
    if (!this.pendingItem) return

    const groupId = Number(event.currentTarget.dataset.groupId)
    const optionId = Number(event.currentTarget.dataset.optionId)
    const group = this.pendingItem.modifier_groups.find((entry) => entry.id === groupId)
    if (!group) return

    const current = [...(this.pendingSelections[groupId] || [])]
    const alreadySelected = current.includes(optionId)

    if (group.multi_select) {
      this.pendingSelections[groupId] = alreadySelected ? current.filter((id) => id !== optionId) : [...current, optionId]
      if (alreadySelected) {
        delete this.pendingOptionPositions[optionId]
      } else {
        this.assignDefaultPosition(group, optionId)
      }
    } else if (alreadySelected && !group.required) {
      this.pendingSelections[groupId] = []
      group.options.forEach((option) => delete this.pendingOptionPositions[option.id])
    } else {
      this.pendingSelections[groupId] = [optionId]
      group.options.forEach((option) => delete this.pendingOptionPositions[option.id])
      this.assignDefaultPosition(group, optionId)
    }

    this.renderComposer()
  }

  selectOptionPosition(event) {
    if (!this.pendingItem) return

    const groupId = Number(event.currentTarget.dataset.groupId)
    const optionId = Number(event.currentTarget.dataset.optionId)
    const positionId = Number(event.currentTarget.dataset.positionId)
    const group = this.pendingItem.modifier_groups.find((entry) => entry.id === groupId)
    if (!group) return

    const selectedIds = this.pendingSelections[groupId] || []
    if (!selectedIds.includes(optionId)) {
      if (group.multi_select) {
        this.pendingSelections[groupId] = [...selectedIds, optionId]
      } else {
        this.pendingSelections[groupId] = [optionId]
        group.options.forEach((option) => delete this.pendingOptionPositions[option.id])
      }
    }

    this.pendingOptionPositions[optionId] = positionId
    this.renderComposer()
  }

  addPendingItem() {
    if (!this.pendingItem) return

    const missingGroup = this.pendingItem.modifier_groups.find((group) => group.required && !(this.pendingSelections[group.id] || []).length)
    if (missingGroup) {
      this.renderComposer(`choose ${missingGroup.name.toLowerCase()} first`)
      return
    }

    const missingPosition = this.pendingItem.modifier_groups.find((group) => {
      if (!group.positions?.length) return false
      return (this.pendingSelections[group.id] || []).some((optionId) => !this.pendingOptionPositions[optionId])
    })
    if (missingPosition) {
      this.renderComposer(`choose a position for each ${missingPosition.name.toLowerCase()} detail`)
      return
    }

    const selectedOptions = this.pendingItem.modifier_groups.flatMap((group) => {
      const ids = this.pendingSelections[group.id] || []
      return group.options.filter((option) => ids.includes(option.id)).map((option) => ({
        group_id: group.id,
        group_name: group.name,
        id: option.id,
        name: option.name,
        price_delta: Number(option.price_delta || 0),
        position_id: this.pendingOptionPositions[option.id] || null,
        position_name: this.positionNameFor(group, this.pendingOptionPositions[option.id]),
        position_price_delta: this.positionPriceFor(option, this.pendingOptionPositions[option.id])
      }))
    })

    const addedQuantity = this.addReceiptLine(this.pendingItem, selectedOptions)
    if (addedQuantity < 1) return
    this.pendingItem = null
    this.pendingSelections = {}
    this.pendingOptionPositions = {}
    this.render()
  }

  cancelPendingItem() {
    this.pendingItem = null
    this.pendingSelections = {}
    this.pendingOptionPositions = {}
    this.renderComposer()
  }

  clearReceipt() {
    this.receipt = []
    this.appliedCouponIds = []
    this.renderReceipt()
  }

  incrementLine(event) {
    const line = this.receipt.find((entry) => entry.signature === event.currentTarget.dataset.signature)
    if (!line) return
    if (line.quantity >= this.maxLineQuantity) {
      this.setNaturalStatus(`Each receipt line can contain at most ${this.maxLineQuantity} identical items.`)
      return
    }
    line.quantity += 1
    this.renderReceipt()
  }

  decrementLine(event) {
    const index = this.receipt.findIndex((entry) => entry.signature === event.currentTarget.dataset.signature)
    if (index === -1) return
    if (this.receipt[index].quantity <= 1) {
      this.receipt.splice(index, 1)
    } else {
      this.receipt[index].quantity -= 1
    }
    this.renderReceipt()
  }

  toggleCoupon(event) {
    const couponId = Number(event.currentTarget.dataset.couponId)
    if (!couponId) return

    if (this.appliedCouponIds.includes(couponId)) {
      this.appliedCouponIds = this.appliedCouponIds.filter((id) => id !== couponId)
    } else {
      this.appliedCouponIds = [...this.appliedCouponIds, couponId]
    }

    this.renderReceipt()
  }

  get maxLineQuantity() {
    const configured = this.hasMaxLineQuantityValue ? Number(this.maxLineQuantityValue) : 0
    return Number.isInteger(configured) && configured > 0 ? configured : DEFAULT_MAX_LINE_QUANTITY
  }

  get maxReceiptLines() {
    const configured = this.hasMaxReceiptLinesValue ? Number(this.maxReceiptLinesValue) : 0
    return Number.isInteger(configured) && configured > 0 ? configured : DEFAULT_MAX_RECEIPT_LINES
  }

  get currentCategory() {
    return this.categories.find((category) => category.id === this.selectedCategoryId) || null
  }

  get currentItems() {
    return this.currentCategory?.items || []
  }

  render() {
    this.renderCategories()
    this.renderItems()
    this.renderComposer()
    this.renderReceipt()
  }

  renderCategories() {
    if (!this.hasCategoryListTarget) return
    if (!this.categories.length) {
      this.categoryListTarget.innerHTML = `<p class="pow-wow-pos-empty">No menu categories yet.</p>`
      return
    }

    this.categoryListTarget.innerHTML = this.categories.map((category) => `
      <button type="button"
              class="pow-wow-order-button ${category.id === this.selectedCategoryId ? "is-active" : ""}"
              data-action="pow-wow-order#selectCategory"
              data-category-id="${category.id}">
        <strong>${this.escape(category.name)}</strong>
        ${category.description ? `<span>${this.escape(category.description)}</span>` : ""}
      </button>
    `).join("")
  }

  renderItems() {
    if (!this.hasItemListTarget) return
    if (!this.currentCategory) {
      this.itemListTarget.innerHTML = `<p class="pow-wow-pos-empty">Choose a category.</p>`
      return
    }
    if (!this.currentItems.length) {
      this.itemListTarget.innerHTML = `<p class="pow-wow-pos-empty">No items in ${this.escape(this.currentCategory.name)} yet.</p>`
      return
    }

    this.itemListTarget.innerHTML = this.currentItems.map((item) => `
      <button type="button"
              class="pow-wow-order-item"
              data-action="pow-wow-order#chooseItem"
              data-item-id="${item.id}">
        ${item.photo_url ? `<span class="pow-wow-order-thumb"><img src="${this.escape(item.photo_url)}" alt="${this.escape(item.name)}"></span>` : ""}
        <span class="pow-wow-order-copy">
          <strong>${this.escape(item.name)}</strong>
          <span>${this.formatAmount(item.base_price)}</span>
          ${item.description ? `<small>${this.escape(item.description)}</small>` : ""}
        </span>
      </button>
    `).join("")
  }

  renderComposer(status = "") {
    if (!this.hasComposerTarget) return
    if (!this.pendingItem) {
      this.composerTarget.innerHTML = `<p class="pow-wow-pos-empty">Tap an item to load its modifier path.</p>`
      return
    }

    const groupsMarkup = this.pendingItem.modifier_groups.map((group) => {
      const selectedIds = this.pendingSelections[group.id] || []
      const requiredTag = group.required ? `<em>required</em>` : `<em>optional</em>`
      const multiTag = group.multi_select ? `<em>multi</em>` : `<em>single</em>`

      return `
        <section class="pow-wow-order-group">
          <header>
            <strong>${this.escape(group.name)}</strong>
            <span>${requiredTag}${multiTag}</span>
          </header>
          ${group.description ? `<p>${this.escape(group.description)}</p>` : ""}
          <div class="pow-wow-order-option-list">
            ${group.options.map((option) => {
              const isSelected = selectedIds.includes(option.id)
              const selectedPositionId = this.pendingOptionPositions[option.id]

              return `
                <div class="pow-wow-order-option-card ${isSelected ? "is-active" : ""}">
                  <button type="button"
                          class="pow-wow-order-option ${isSelected ? "is-active" : ""}"
                          data-action="pow-wow-order#toggleOption"
                          data-group-id="${group.id}"
                          data-option-id="${option.id}">
                    <strong>${this.escape(option.name)}</strong>
                    <span>${this.signedAmount(option.price_delta)}</span>
                    ${option.description ? `<small>${this.escape(option.description)}</small>` : ""}
                  </button>
                  ${group.positions?.length ? `
                    <div class="pow-wow-order-position-list">
                      ${group.positions.map((position) => {
                        const positionDelta = this.positionPriceFor(option, position.id)
                        return `
                          <button type="button"
                                  class="pow-wow-order-position-button ${selectedPositionId === position.id ? "is-active" : ""}"
                                  data-action="pow-wow-order#selectOptionPosition"
                                  data-group-id="${group.id}"
                                  data-option-id="${option.id}"
                                  data-position-id="${position.id}">
                            ${this.escape(position.name)}
                            ${positionDelta ? `<small>${this.signedAmount(positionDelta)}</small>` : ""}
                          </button>
                        `
                      }).join("")}
                    </div>
                  ` : ""}
                </div>
              `
            }).join("")}
          </div>
        </section>
      `
    }).join("")

    this.composerTarget.innerHTML = `
      <div class="pow-wow-order-composer-card">
        <div class="pow-wow-order-composer-head">
          <div>
            <strong>${this.escape(this.pendingItem.name)}</strong>
            <span>${this.formatAmount(this.pendingItem.base_price)}</span>
          </div>
          ${status ? `<p class="pow-wow-order-status">${this.escape(status)}</p>` : ""}
        </div>
        ${this.pendingItem.description ? `<p class="pow-wow-order-description">${this.escape(this.pendingItem.description)}</p>` : ""}
        ${groupsMarkup}
        <div class="pow-wow-order-composer-actions">
          <button type="button" class="pow-wow-btn pow-wow-btn-slim" data-action="pow-wow-order#cancelPendingItem">cancel</button>
          <button type="button" class="pow-wow-btn" data-action="pow-wow-order#addPendingItem">add to receipt ${this.formatAmount(this.pendingTotal())}</button>
        </div>
      </div>
    `
  }

  pendingTotal() {
    if (!this.pendingItem) return 0
    const deltas = this.pendingItem.modifier_groups.flatMap((group) => {
      const ids = this.pendingSelections[group.id] || []
      return group.options.filter((option) => ids.includes(option.id)).flatMap((option) => [
        Number(option.price_delta || 0),
        this.positionPriceFor(option, this.pendingOptionPositions[option.id])
      ])
    })
    return Number(this.pendingItem.base_price || 0) + deltas.reduce((sum, value) => sum + value, 0)
  }

  addReceiptLine(item, selectedOptions, quantity = 1) {
    const optionIds = selectedOptions
      .map((option) => `${option.id}@${option.position_id || 0}`)
      .sort((a, b) => a.localeCompare(b))
    const signature = `${item.id}:${optionIds.join(",")}`
    const existing = this.receipt.find((line) => line.signature === signature)
    const requestedQuantity = Math.max(1, Math.floor(Number(quantity) || 1))

    if (!existing && this.receipt.length >= this.maxReceiptLines) {
      this.setNaturalStatus(`A receipt can contain at most ${this.maxReceiptLines} distinct lines.`)
      return 0
    }

    const normalizedQuantity = Math.min(
      requestedQuantity,
      this.maxLineQuantity - Number(existing?.quantity || 0)
    )
    if (normalizedQuantity < 1) {
      this.setNaturalStatus(`Each receipt line can contain at most ${this.maxLineQuantity} identical items.`)
      return 0
    }

    if (existing) {
      existing.quantity += normalizedQuantity
    } else {
      const unitPrice = Number(item.base_price || 0) + selectedOptions.reduce((sum, option) => sum + Number(option.price_delta || 0) + Number(option.position_price_delta || 0), 0)
      this.receipt.push({
        signature,
        itemId: item.id,
        name: item.name,
        quantity: normalizedQuantity,
        unitPrice,
        options: selectedOptions.map((option) => ({
          groupId: option.group_id,
          optionId: option.id,
          positionId: option.position_id,
          groupName: option.group_name,
          name: option.name,
          positionName: option.position_name
        }))
      })
    }

    this.renderReceipt()
    if (normalizedQuantity < requestedQuantity) {
      this.setNaturalStatus(`That receipt line was limited to ${this.maxLineQuantity} identical items.`)
    }
    return normalizedQuantity
  }

  renderReceipt() {
    if (!this.hasReceiptListTarget || !this.hasReceiptTotalTarget) return
    if (!this.receipt.length) {
      this.receiptListTarget.innerHTML = `<p class="pow-wow-pos-empty">Receipt is empty.</p>`
      this.receiptTotalTarget.textContent = this.formatAmount(0)
      this.renderCoupons(0, 0)
      this.dispatch("total-updated", { detail: { total: 0 } })
      this.dispatch("receipt-changed", { detail: { order: this.currentOrderPayload(0, 0) } })
      return
    }

    this.receiptListTarget.innerHTML = this.receipt.map((line) => `
      <article class="pow-wow-order-line">
        <div class="pow-wow-order-line-head">
          <div>
            <strong>${line.quantity}x ${this.escape(line.name)}</strong>
            <span>${this.formatAmount(line.unitPrice * line.quantity)}</span>
          </div>
          <div class="pow-wow-order-line-actions">
            <button type="button" class="pow-wow-btn pow-wow-btn-slim" data-action="pow-wow-order#decrementLine" data-signature="${line.signature}">-</button>
            <button type="button"
                    class="pow-wow-btn pow-wow-btn-slim"
                    data-action="pow-wow-order#incrementLine"
                    data-signature="${line.signature}"
                    ${line.quantity >= this.maxLineQuantity ? "disabled" : ""}>+</button>
          </div>
        </div>
        ${line.options.length ? `
          <ul class="pow-wow-order-line-options">
            ${line.options.map((option) => `<li>${this.escape(option.groupName)}: ${this.escape(option.name)}${option.positionName ? ` [${this.escape(option.positionName)}]` : ""}</li>`).join("")}
          </ul>
        ` : `<p class="pow-wow-order-line-options is-empty">base build</p>`}
      </article>
    `).join("")

    const total = this.receipt.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0)
    const discountedTotal = this.applyCoupons(total)
    this.receiptTotalTarget.textContent = this.formatAmount(discountedTotal)
    this.renderCoupons(total, discountedTotal)
    this.dispatch("total-updated", { detail: { total: discountedTotal } })
    this.dispatch("receipt-changed", { detail: { order: this.currentOrderPayload(total, discountedTotal) } })
  }

  renderCoupons(subtotal, discountedTotal) {
    if (!this.hasCouponListTarget) return
    if (!this.coupons.length) {
      this.couponListTarget.innerHTML = ""
      return
    }

    this.couponListTarget.innerHTML = this.coupons.map((coupon) => {
      const isActive = this.appliedCouponIds.includes(coupon.id)
      const parts = []
      if (Number(coupon.amount_delta || 0) !== 0) parts.push(`MAZA ${this.signedAmount(coupon.amount_delta)}`)
      if (Number(coupon.percent_delta || 0) !== 0) parts.push(`% ${this.signedPercent(coupon.percent_delta)}`)

      return `
        <button type="button"
                class="pow-wow-order-coupon-toggle ${isActive ? "is-active" : ""}"
                data-action="pow-wow-order#toggleCoupon"
                data-coupon-id="${coupon.id}">
          <strong>${this.escape(coupon.code)}</strong>
          <span>${parts.join(" / ") || "custom"}</span>
          ${coupon.description ? `<small>${this.escape(coupon.description)}</small>` : ""}
        </button>
      `
    }).join("") + `
      <div class="pow-wow-order-coupon-summary">
        <span>subtotal ${this.formatAmount(subtotal)}</span>
        <strong>checkout ${this.formatAmount(discountedTotal)}</strong>
      </div>
    `
  }

  applyCoupons(subtotal) {
    return this.appliedCoupons().reduce((runningTotal, coupon) => {
      let nextTotal = runningTotal + Number(coupon.amount_delta || 0)
      if (Number(coupon.percent_delta || 0) !== 0) {
        nextTotal = nextTotal * (1 + Number(coupon.percent_delta || 0) / 100)
      }
      return Math.max(nextTotal, 0)
    }, subtotal)
  }

  appliedCoupons() {
    return this.coupons.filter((coupon) => this.appliedCouponIds.includes(coupon.id))
  }

  currentOrderPayload(subtotal, total) {
    return {
      lines: this.receipt.map((line) => ({
        item_id: line.itemId,
        name: line.name,
        quantity: line.quantity,
        unit_price: Number(line.unitPrice || 0),
        options: line.options.map((option) => ({
          modifier_group_id: option.groupId,
          option_id: option.optionId,
          position_id: option.positionId,
          group_name: option.groupName,
          name: option.name,
          position_name: option.positionName
        }))
      })),
      coupon_codes: this.appliedCoupons().map((coupon) => coupon.code),
      subtotal,
      total
    }
  }

  formatAmount(value) {
    return `${Number(value || 0).toFixed(5).replace(/\.?0+$/, "")} MAZA`
  }

  signedAmount(value) {
    const numeric = Number(value || 0)
    if (numeric > 0) return `+${this.formatAmount(numeric)}`
    if (numeric < 0) return `-${this.formatAmount(Math.abs(numeric))}`
    return this.formatAmount(0)
  }

  signedPercent(value) {
    const numeric = Number(value || 0)
    if (numeric > 0) return `+${numeric}%`
    if (numeric < 0) return `${numeric}%`
    return "0%"
  }

  escape(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  assignDefaultPosition(group, optionId) {
    const defaultPositionId = Number(group.default_position_id || this.defaultPosition(group)?.id || 0)
    if (!defaultPositionId) return

    this.pendingOptionPositions[optionId] ||= defaultPositionId
  }

  defaultPosition(group) {
    if (!group?.positions?.length) return null
    return group.positions.find((position) => String(position.name || "").trim().toUpperCase() === "ALL") || group.positions[0]
  }

  positionNameFor(group, positionId) {
    if (!positionId) return ""
    return group.positions?.find((position) => position.id === positionId)?.name || ""
  }

  positionPriceFor(option, positionId) {
    if (!positionId) return 0
    const raw = option.position_prices?.[positionId] ?? option.position_prices?.[String(positionId)] ?? 0
    return Number(raw || 0)
  }
}
